// src/components/duty/DutySurface.tsx — Sign-In System (Build 1) Home duty surface.
//
// The orchestrator + all net-new duty UI ported from the prototype slices
// (DutySlot, SignInSheet, ConfirmAndSignInModal, LoneWorkingPanel) to live TS.
// Mounted by HomePage in the top-of-body slot. Renders NOTHING when the user is
// ineligible or the feature is off — silent gating (PHASE_8_ADDENDUM §9d).
//
// Live timers use Date.now() deltas, never tick accumulators. Sign-off uses a
// styled modal, never window.confirm(). Lone Working is brass; duty is sage.

import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch, ApiError } from "../../api";
import type { MeResponse, DutySessionSummary } from "../../auth";
import { D, DF, DUTY_KEYFRAMES, formatClock, formatDuration, formatLongDate } from "./dutyTokens";

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

interface BootstrapRole { code: string; displayName: string; defaultLocationCode: string | null; lastUsedAt: string | null; }
interface BootstrapLocation { code: string; displayName: string; isOnTrain: boolean; sortOrder: number; }
interface BootstrapData {
  roles: BootstrapRole[];
  locations: BootstrapLocation[];
  lastUsedRoleCode: string | null;
  medicalDeclarationText: string;
  medicalDeclarationVersion: number;
}
interface LwActive {
  id: number; windowMinutes: number; startedAt: string; checkInDueAt: string;
  lineManagerFirstName: string | null; lineManagerLastName: string | null; notifyAtLocal: string;
}
interface LwState {
  enabled: boolean; onDuty: boolean; canEnable: boolean; windowOptions: number[];
  lineManager: { firstName: string | null; lastName: string | null; hasPhone: boolean };
  loneWorking: LwActive | null;
}

type SheetMode = "sign-in" | "edit-role" | "edit-location";

// ---------------------------------------------------------------------------
// Orchestrator
// ---------------------------------------------------------------------------

export function DutySurface({ me, onChanged }: { me: MeResponse | null; onChanged?: () => void }) {
  const eligible = !!me?.signIn?.eligible && me?.features?.dutySignin !== false;

  const [session, setSession] = useState<DutySessionSummary | null>(
    me && me.dutySession?.active ? me.dutySession : null,
  );
  const [lw, setLw] = useState<LwState | null>(null);
  const [bootstrap, setBootstrap] = useState<BootstrapData | null>(null);
  const [sheetMode, setSheetMode] = useState<SheetMode | null>(null);
  const [confirm, setConfirm] = useState<{ roleCodes: string[]; locationCode: string } | null>(null);
  const [signOffOpen, setSignOffOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast((t) => (t === msg ? null : t)), 3200);
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const r = await apiFetch<{ dutySession: DutySessionSummary }>("/api/duty-sessions/me");
      setSession(r.dutySession?.active ? r.dutySession : null);
    } catch {
      /* leave existing */
    }
  }, []);

  const refreshLw = useCallback(async () => {
    try {
      const r = await apiFetch<LwState>("/api/lone-working/me");
      setLw(r);
    } catch {
      setLw(null);
    }
  }, []);

  useEffect(() => {
    if (!eligible) return;
    void refreshSession();
  }, [eligible, refreshSession]);

  useEffect(() => {
    if (eligible && session?.active) void refreshLw();
    else setLw(null);
  }, [eligible, session?.active, refreshLw]);

  if (!eligible) return null;

  // ---- sign-in flow ----
  const openSignInSheet = async () => {
    try {
      const b = await apiFetch<BootstrapData>("/api/duty-sessions/bootstrap");
      setBootstrap(b);
      setSheetMode("sign-in");
    } catch (e) {
      flash(e instanceof ApiError ? e.message : "Couldn't open sign-in.");
    }
  };

  const openEditSheet = async (mode: SheetMode) => {
    try {
      const b = await apiFetch<BootstrapData>("/api/duty-sessions/bootstrap");
      setBootstrap(b);
      setSheetMode(mode);
    } catch (e) {
      flash(e instanceof ApiError ? e.message : "Couldn't open editor.");
    }
  };

  const onSheetConfirm = async ({ roleCodes, locationCode }: { roleCodes: string[]; locationCode: string }) => {
    if (sheetMode === "sign-in") {
      setConfirm({ roleCodes, locationCode });
      return;
    }
    // edit mode → PATCH directly
    setBusy(true);
    try {
      const r = await apiFetch<{ dutySession: DutySessionSummary }>("/api/duty-sessions/me", {
        method: "PATCH",
        body: JSON.stringify({ roleCodes, locationCode }),
      });
      setSession(r.dutySession);
      setSheetMode(null);
      flash("Duty updated");
    } catch (e) {
      flash(e instanceof ApiError ? e.message : "Couldn't update.");
    } finally {
      setBusy(false);
    }
  };

  const doSignIn = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const r = await apiFetch<{ dutySession: DutySessionSummary }>("/api/duty-sessions", {
        method: "POST",
        body: JSON.stringify({ ...confirm, medicalDeclarationConfirmed: true }),
      });
      setSession(r.dutySession);
      setConfirm(null);
      setSheetMode(null);
      onChanged?.();
      void refreshLw();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        await refreshSession();
        setConfirm(null);
        setSheetMode(null);
      } else {
        flash(e instanceof ApiError ? e.message : "Couldn't sign in.");
      }
    } finally {
      setBusy(false);
    }
  };

  const doSignOff = async () => {
    setBusy(true);
    try {
      await apiFetch("/api/duty-sessions/me", { method: "DELETE" });
      setSession(null);
      setLw(null);
      setSignOffOpen(false);
      onChanged?.();
    } catch (e) {
      flash(e instanceof ApiError ? e.message : "Couldn't sign off.");
    } finally {
      setBusy(false);
    }
  };

  const ackReminder = async () => {
    setSession((s) => (s ? { ...s, shouldShowReminder: false } : s));
    try { await apiFetch("/api/duty-sessions/me/ack-reminder", { method: "POST" }); } catch { /* best effort */ }
  };

  // ---- lone working ----
  const lwEnable = async (windowMinutes: number) => {
    try {
      await apiFetch("/api/lone-working", { method: "POST", body: JSON.stringify({ windowMinutes }) });
      void refreshLw();
    } catch (e) {
      flash(e instanceof ApiError ? e.message : "Couldn't enable Lone Working.");
    }
  };
  const lwChangeWindow = async (windowMinutes: number) => {
    try {
      await apiFetch("/api/lone-working/me", { method: "PATCH", body: JSON.stringify({ windowMinutes }) });
      void refreshLw();
    } catch (e) {
      flash(e instanceof ApiError ? e.message : "Couldn't change window.");
    }
  };
  const lwSwitchOff = async () => {
    try {
      await apiFetch("/api/lone-working/me", { method: "DELETE" });
      void refreshLw();
    } catch (e) {
      flash(e instanceof ApiError ? e.message : "Couldn't switch off.");
    }
  };

  const lonePanel = session?.active ? (
    <LoneWorkingPanel
      loneWorking={lw?.loneWorking ?? null}
      canEnable={!!lw?.canEnable}
      defaultWindow={lw?.windowOptions?.[1] ?? 240}
      windowOptions={lw?.windowOptions ?? [120, 240, 360, 480]}
      onEnable={lwEnable}
      onChangeWindow={lwChangeWindow}
      onSwitchOff={lwSwitchOff}
    />
  ) : null;

  return (
    <div>
      <style>{DUTY_KEYFRAMES}</style>

      <DutySlot
        dutySession={session}
        onSignInClick={openSignInSheet}
        onSignOffClick={() => setSignOffOpen(true)}
        onChangeRoleClick={() => openEditSheet("edit-role")}
        onChangeLocClick={() => openEditSheet("edit-location")}
        showReminder={!!session?.shouldShowReminder}
        onAckReminder={ackReminder}
        loneWorkingPanel={lonePanel}
      />

      {sheetMode && bootstrap && (
        <SignInSheet
          mode={sheetMode}
          roles={bootstrap.roles}
          locations={bootstrap.locations}
          initialRoleCodes={
            sheetMode === "sign-in"
              ? bootstrap.lastUsedRoleCode
                ? [bootstrap.lastUsedRoleCode]
                : []
              : session?.roles?.map((r) => r.code) ?? (session?.roleCode ? [session.roleCode] : [])
          }
          initialLocationCode={sheetMode !== "sign-in" ? session?.locationCode ?? null : null}
          onCancel={() => setSheetMode(null)}
          onConfirmStart={onSheetConfirm}
        />
      )}

      {confirm && bootstrap && (
        <ConfirmAndSignInModal
          declarationText={bootstrap.medicalDeclarationText}
          busy={busy}
          onCancel={() => setConfirm(null)}
          onConfirm={doSignIn}
        />
      )}

      {signOffOpen && (
        <SignOffConfirmModal busy={busy} onCancel={() => setSignOffOpen(false)} onConfirm={doSignOff} />
      )}

      {toast && <DutyToast text={toast} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DutySlot — signed-out brass CTA OR signed-in sage status card
// ---------------------------------------------------------------------------

function DutySlot({
  dutySession, onSignInClick, onSignOffClick, onChangeRoleClick, onChangeLocClick,
  showReminder, onAckReminder, loneWorkingPanel,
}: {
  dutySession: DutySessionSummary | null;
  onSignInClick: () => void;
  onSignOffClick: () => void;
  onChangeRoleClick: () => void;
  onChangeLocClick: () => void;
  showReminder: boolean;
  onAckReminder: () => void;
  loneWorkingPanel: React.ReactNode;
}) {
  if (!dutySession || !dutySession.active) {
    return (
      <button
        type="button"
        onClick={onSignInClick}
        aria-label="Sign in for duty. Tap to start."
        style={{
          display: "flex", alignItems: "center", gap: 16, width: "100%", padding: "22px 22px 20px",
          background: "linear-gradient(180deg, #8C6210 0%, #6F4A0A 100%)", color: D.brassGlow,
          borderRadius: 18, border: "1.5px solid #5C3D08",
          boxShadow: "0 4px 14px rgba(122,84,11,0.28), inset 0 1px 0 rgba(244,226,168,0.25)",
          cursor: "pointer", textAlign: "left",
        }}
      >
        <span style={{
          width: 52, height: 52, borderRadius: 14, background: "rgba(244,226,168,0.16)",
          border: "1px solid rgba(244,226,168,0.32)", display: "flex", alignItems: "center",
          justifyContent: "center", flexShrink: 0,
        }}>
          <ClockIcon size={26} color={D.brassGlow} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontFamily: DF.serif, fontSize: 22, fontWeight: 700, letterSpacing: "-0.4px", lineHeight: 1.05 }}>
            Sign in for Duty
          </span>
          <span style={{ display: "block", fontFamily: DF.body, fontSize: 11.5, fontWeight: 700, letterSpacing: "1.4px", textTransform: "uppercase", color: "rgba(244,226,168,0.7)", marginTop: 6 }}>
            Tap to start — under 5 seconds
          </span>
        </span>
        <span style={{ color: D.brassGlow, fontSize: 22, fontWeight: 800, opacity: 0.7 }}>›</span>
      </button>
    );
  }

  return (
    <section
      role="region"
      aria-label={`On duty since ${dutySession.signedInAt}, role ${dutySession.roleDisplayName}, location ${dutySession.locationDisplayName}`}
      style={{
        width: "100%", background: `linear-gradient(180deg, ${D.dutySurf} 0%, ${D.dutyBg} 100%)`,
        border: `1.5px solid ${D.duty}`, borderRadius: 18, padding: "16px 16px 14px",
        boxShadow: "0 4px 14px rgba(79,104,82,0.16)",
      }}
    >
      <DutyHead signedInAt={dutySession.signedInAt!} />
      <LiveClock />
      <SignedInMeta signedInAt={dutySession.signedInAt!} />
      <PillRow dutySession={dutySession} onChangeRoleClick={onChangeRoleClick} onChangeLocClick={onChangeLocClick} />
      {showReminder && <DutyReminder onAck={onAckReminder} onSignOff={onSignOffClick} />}
      {loneWorkingPanel}
      <button
        type="button"
        onClick={onSignOffClick}
        style={{
          width: "100%", background: D.card, border: `1.5px solid ${D.duty}`, borderRadius: 12,
          padding: "11px 14px", fontFamily: DF.body, fontSize: 14, fontWeight: 800, color: D.dutyDk,
          letterSpacing: "0.2px", cursor: "pointer", display: "flex", alignItems: "center",
          justifyContent: "center", gap: 8,
        }}
      >
        <SignOffIcon size={16} /> Sign off duty
      </button>
    </section>
  );
}

function DutyHead({ signedInAt }: { signedInAt: string }) {
  const durationMin = useDurationMinutes(signedInAt);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
      <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: "50%", background: D.duty, animation: "dutyPulse 2.4s ease-out infinite" }} />
      <span style={{ flex: 1, fontFamily: DF.body, fontSize: 10.5, fontWeight: 800, letterSpacing: "1.6px", textTransform: "uppercase", color: D.dutyDk }}>
        On duty
      </span>
      <span style={{ fontFamily: DF.mono, fontSize: 14, fontWeight: 700, color: D.dutyDk, letterSpacing: "0.5px" }} aria-live="polite">
        {formatDuration(durationMin)}
      </span>
    </div>
  );
}

function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <div aria-live="off" style={{ fontFamily: DF.mono, fontSize: 36, fontWeight: 700, color: D.ink, letterSpacing: "-0.5px", lineHeight: 1, margin: "4px 0 2px" }}>
      {formatClock(now)}
    </div>
  );
}

function SignedInMeta({ signedInAt }: { signedInAt: string }) {
  const d = new Date(signedInAt);
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return (
    <div style={{ fontFamily: DF.serif, fontStyle: "italic", fontSize: 13, fontWeight: 600, color: D.dutyDk, marginBottom: 12 }}>
      signed in at {time} · {formatLongDate(d)}
    </div>
  );
}

function PillRow({ dutySession, onChangeRoleClick, onChangeLocClick }: { dutySession: DutySessionSummary; onChangeRoleClick: () => void; onChangeLocClick: () => void; }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "10px 0", borderTop: "1px dashed rgba(79,104,82,0.28)", borderBottom: "1px dashed rgba(79,104,82,0.28)", marginBottom: 12 }}>
      <DutyPill
        label={dutySession.roles && dutySession.roles.length > 1 ? "Roles" : "Role"}
        value={
          dutySession.roles && dutySession.roles.length > 0
            ? dutySession.roles.map((r) => r.displayName).join(", ")
            : dutySession.roleDisplayName ?? ""
        }
        onChange={onChangeRoleClick}
      />
      <DutyPill label="Where" value={dutySession.locationDisplayName ?? ""} onChange={onChangeLocClick} />
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", background: D.duty, color: "#fff", border: `1px solid ${D.dutyDk}`, borderRadius: 999, fontFamily: DF.body, fontSize: 12, fontWeight: 700 }}>
        <CheckIcon size={12} color="#fff" /> Confirmed fit
      </span>
    </div>
  );
}

function DutyPill({ label, value, onChange }: { label: string; value: string; onChange: () => void }) {
  return (
    <button type="button" onClick={onChange} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", background: "rgba(255,255,255,0.55)", border: "1px solid rgba(79,104,82,0.18)", borderRadius: 999, fontFamily: DF.body, fontSize: 12, fontWeight: 700, color: D.ink, cursor: "pointer" }}>
      <span style={{ fontFamily: DF.mono, fontSize: 9, fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: D.dutyDk }}>{label}</span>
      {value}
      <span style={{ color: D.duty, fontWeight: 800, fontSize: 11 }}>↻</span>
    </button>
  );
}

function DutyReminder({ onAck, onSignOff }: { onAck: () => void; onSignOff: () => void }) {
  return (
    <div style={{ background: D.card, border: `1.5px solid ${D.brassDk}`, borderLeftWidth: 5, borderRadius: 14, padding: "12px 14px", display: "flex", gap: 10, alignItems: "flex-start", boxShadow: "0 4px 14px rgba(122,84,11,0.14)", marginBottom: 12 }}>
      <span style={{ width: 30, height: 30, borderRadius: 8, background: D.brassBg, border: `1px solid ${D.brassLt}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
        <ClockIcon size={16} color={D.brassDk} />
      </span>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: DF.serif, fontSize: 14.5, fontWeight: 700, color: D.ink }}>Still on duty?</div>
        <div style={{ fontFamily: DF.body, fontSize: 12.5, color: D.inkSoft, marginTop: 2, lineHeight: 1.4 }}>
          You've been signed in for 6 hours. Confirm or sign off. Auto sign-off at 8 h.
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <button onClick={onSignOff} style={ghostBtnSm()}>Sign off now</button>
          <button onClick={onAck} style={brassBtnSm()}>Yes, still on</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lone Working panel (brass)
// ---------------------------------------------------------------------------

function LoneWorkingPanel({
  loneWorking, canEnable, defaultWindow, windowOptions, onEnable, onChangeWindow, onSwitchOff,
}: {
  loneWorking: LwActive | null;
  canEnable: boolean;
  defaultWindow: number;
  windowOptions: number[];
  onEnable: (windowMinutes: number) => void;
  onChangeWindow: (windowMinutes: number) => void;
  onSwitchOff: () => void;
}) {
  if (!loneWorking) {
    return (
      <button
        type="button"
        disabled={!canEnable}
        onClick={() => onEnable(defaultWindow)}
        aria-label="Enable lone working safety check-in"
        style={{
          display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 12px",
          borderRadius: 12, cursor: canEnable ? "pointer" : "not-allowed", marginBottom: 8,
          border: "1.5px dashed rgba(122,84,11,0.45)", background: "rgba(255,255,255,0.55)",
          opacity: canEnable ? 1 : 0.5, textAlign: "left",
        }}
      >
        <span style={{ width: 11, height: 11, borderRadius: "50%", background: D.hairline, flexShrink: 0 }} />
        <span style={{ flex: 1 }}>
          <span style={{ display: "block", fontFamily: DF.serif, fontSize: 13.5, fontWeight: 700, color: D.ink }}>Lone Working</span>
          <span style={{ display: "block", fontFamily: DF.body, fontSize: 10.5, fontWeight: 600, color: D.inkSoft, marginTop: 1 }}>
            {canEnable ? "Off · tap to enable safety check-in" : "Set a line manager with a phone first"}
          </span>
        </span>
        <span style={{ fontSize: 16, fontWeight: 700, color: D.brassDk }}>›</span>
      </button>
    );
  }
  return <ActiveLoneWorking loneWorking={loneWorking} windowOptions={windowOptions} onChangeWindow={onChangeWindow} onSwitchOff={onSwitchOff} />;
}

function ActiveLoneWorking({
  loneWorking, windowOptions, onChangeWindow, onSwitchOff,
}: {
  loneWorking: LwActive; windowOptions: number[];
  onChangeWindow: (windowMinutes: number) => void; onSwitchOff: () => void;
}) {
  const dueMs = useMemo(() => new Date(loneWorking.checkInDueAt).getTime(), [loneWorking.checkInDueAt]);
  const [remaining, setRemaining] = useState(() => Math.max(0, dueMs - Date.now()));
  useEffect(() => {
    const tick = () => setRemaining(Math.max(0, dueMs - Date.now()));
    const id = window.setInterval(tick, 1000);
    tick();
    return () => window.clearInterval(id);
  }, [dueMs]);

  const hh = Math.floor(remaining / 3600000);
  const mm = Math.floor((remaining % 3600000) / 60000);
  const ss = Math.floor((remaining % 60000) / 1000);
  const cd = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  const mgr = `${loneWorking.lineManagerFirstName ?? ""} ${loneWorking.lineManagerLastName ?? ""}`.trim() || "your line manager";

  return (
    <>
      <button type="button" onClick={onSwitchOff} aria-label="Switch off lone working" style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 12px", borderRadius: 12, cursor: "pointer", marginBottom: 8, border: `1.5px solid ${D.brassDk}`, background: D.brassBg, textAlign: "left" }}>
        <span aria-hidden="true" style={{ width: 11, height: 11, borderRadius: "50%", background: D.brassDk, flexShrink: 0, animation: "lwPulse 2s ease-out infinite" }} />
        <span style={{ flex: 1 }}>
          <span style={{ display: "block", fontFamily: DF.serif, fontSize: 13.5, fontWeight: 700, color: D.ink }}>Lone Working — ON</span>
          <span style={{ display: "block", fontFamily: DF.body, fontSize: 10.5, fontWeight: 700, color: D.brassDk, marginTop: 1 }}>
            {loneWorking.windowMinutes / 60}-hour window · tap to switch off
          </span>
        </span>
        <span style={{ fontSize: 16, fontWeight: 700, color: D.brassDk }}>›</span>
      </button>

      <div style={{ marginTop: 4, padding: "12px 14px", background: D.brassBg, border: `1.5px solid ${D.brassDk}`, borderRadius: 14, textAlign: "center" }}>
        <div style={{ fontFamily: DF.body, fontSize: 9.5, fontWeight: 800, letterSpacing: "1.4px", textTransform: "uppercase", color: D.brassDk, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <ClockIcon size={10} color={D.brassDk} /> Check-in due in
        </div>
        <div aria-live="polite" style={{ fontFamily: DF.mono, fontSize: 28, fontWeight: 700, color: D.ink, letterSpacing: "-0.5px", margin: "4px 0 2px" }}>{cd}</div>
        <div style={{ fontFamily: DF.serif, fontStyle: "italic", fontSize: 12, color: D.inkSoft, lineHeight: 1.4 }}>
          If not switched off, <strong>{mgr}</strong> (your line manager) will be texted at {loneWorking.notifyAtLocal}.
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
          {windowOptions.map((mins) => {
            const selected = mins === loneWorking.windowMinutes;
            return (
              <button key={mins} type="button" onClick={() => !selected && onChangeWindow(mins)} style={{ flex: 1, padding: "7px 4px", background: selected ? D.brassDk : D.card, border: `1.5px solid ${selected ? D.brassDk : D.hairline}`, borderRadius: 10, cursor: selected ? "default" : "pointer", textAlign: "center" }}>
                <span style={{ display: "block", fontFamily: DF.mono, fontSize: 14, fontWeight: 700, color: selected ? D.brassGlow : D.ink, letterSpacing: "-0.3px" }}>{mins / 60} h</span>
                <span style={{ display: "block", fontFamily: DF.body, fontSize: 8.5, fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: selected ? "rgba(244,226,168,0.85)" : D.inkMute, marginTop: 1 }}>{selected ? "Current" : "Window"}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// SignInSheet (bottom sheet)
// ---------------------------------------------------------------------------

function SignInSheet({
  mode, roles, locations, initialRoleCodes, initialLocationCode, onCancel, onConfirmStart,
}: {
  mode: SheetMode;
  roles: BootstrapRole[];
  locations: BootstrapLocation[];
  initialRoleCodes: string[];
  initialLocationCode: string | null;
  onCancel: () => void;
  onConfirmStart: (sel: { roleCodes: string[]; locationCode: string }) => void;
}) {
  const defaultLocFor = (rc: string | undefined) => roles.find((r) => r.code === rc)?.defaultLocationCode ?? null;
  const initialRoles = initialRoleCodes.length ? initialRoleCodes : roles[0] ? [roles[0].code] : [];
  const [roleCodes, setRoleCodes] = useState<string[]>(initialRoles);
  const [locationCode, setLocationCode] = useState<string>(initialLocationCode || defaultLocFor(initialRoles[0]) || locations[0]?.code || "");
  const [manualLoc, setManualLoc] = useState(false);

  const primaryRole = roleCodes[0];

  // Location follows the PRIMARY (first-selected) role's default unless the user
  // has picked a location manually.
  useEffect(() => {
    if (!manualLoc) {
      const d = defaultLocFor(primaryRole);
      if (d) setLocationCode(d);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryRole]);

  const toggleRole = (code: string) => {
    setRoleCodes((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const currentRoleName = roles.find((r) => r.code === primaryRole)?.displayName ?? "";
  const primaryLabel = mode === "sign-in" ? "Sign in" : "Save changes";
  const canConfirm = roleCodes.length > 0 && !!locationCode;
  const todayLabel = new Date().toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }).toUpperCase();

  return (
    <div role="dialog" aria-label="Sign in for duty" onClick={onCancel} style={overlayBottom()}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 520, background: D.page, borderRadius: "22px 22px 0 0", padding: "16px 18px 20px", boxShadow: "0 -16px 40px rgba(122,84,11,0.18)" }}>
        <div style={{ width: 38, height: 4, borderRadius: 2, background: D.hairline, margin: "-6px auto 14px" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontFamily: DF.serif, fontStyle: "italic", fontSize: 14, fontWeight: 700, color: D.dutyDk }}>
            {mode === "sign-in" ? "Sign in for duty" : "Update your duty status"}
          </span>
          <span style={{ fontFamily: DF.body, fontSize: 10.5, fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase", color: D.inkMute }}>{todayLabel}</span>
        </div>

        <SheetSection label={roleCodes.length > 1 ? "Roles" : "Role"} hint="Tap to select — pick more than one if needed">
          <SheetMultiPillRow items={roles.map((r) => ({ key: r.code, label: r.displayName }))} selectedKeys={roleCodes} onToggle={toggleRole} />
        </SheetSection>
        <SheetSection label="Location" hint={currentRoleName ? `Default for ${currentRoleName}` : "Where are you?"}>
          <SheetPillRow items={locations.map((l) => ({ key: l.code, label: l.displayName }))} selectedKey={locationCode} onSelect={(c) => { setLocationCode(c); setManualLoc(true); }} />
        </SheetSection>

        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button onClick={onCancel} style={ghostBtnLg()}>Cancel</button>
          <button
            onClick={() => canConfirm && onConfirmStart({ roleCodes, locationCode })}
            disabled={!canConfirm}
            style={{ ...primaryBtn(), opacity: canConfirm ? 1 : 0.6, cursor: canConfirm ? "pointer" : "not-allowed" }}
          >
            {primaryLabel}<span style={{ marginLeft: 6 }}>→</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function SheetSection({ label, hint, children }: { label: string; hint: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontFamily: DF.body, fontSize: 10.5, fontWeight: 700, letterSpacing: "1.4px", textTransform: "uppercase", color: D.inkMute }}>{label}</span>
        <span style={{ fontFamily: DF.serif, fontStyle: "italic", fontSize: 11, fontWeight: 600, color: D.brassDk }}>{hint}</span>
      </div>
      {children}
    </div>
  );
}

function SheetPillRow({ items, selectedKey, onSelect }: { items: { key: string; label: string }[]; selectedKey: string; onSelect: (key: string) => void }) {
  return (
    <div role="radiogroup" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
      {items.map((it) => {
        const selected = it.key === selectedKey;
        return (
          <button key={it.key} role="radio" aria-checked={selected} onClick={() => onSelect(it.key)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", background: selected ? D.duty : D.card, border: `1.5px solid ${selected ? D.dutyDk : D.hairline}`, borderRadius: 999, fontFamily: DF.body, fontSize: 13.5, fontWeight: 700, color: selected ? "#fff" : D.inkSoft, cursor: "pointer", whiteSpace: "nowrap", boxShadow: selected ? "0 2px 6px rgba(79,104,82,0.32)" : "none" }}>
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

// Multi-select variant — toggle pills (a session can carry several roles).
function SheetMultiPillRow({ items, selectedKeys, onToggle }: { items: { key: string; label: string }[]; selectedKeys: string[]; onToggle: (key: string) => void }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
      {items.map((it) => {
        const selected = selectedKeys.includes(it.key);
        return (
          <button key={it.key} type="button" aria-pressed={selected} onClick={() => onToggle(it.key)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", background: selected ? D.duty : D.card, border: `1.5px solid ${selected ? D.dutyDk : D.hairline}`, borderRadius: 999, fontFamily: DF.body, fontSize: 13.5, fontWeight: 700, color: selected ? "#fff" : D.inkSoft, cursor: "pointer", whiteSpace: "nowrap", boxShadow: selected ? "0 2px 6px rgba(79,104,82,0.32)" : "none" }}>
            {selected && <CheckIcon size={12} color="#fff" />}
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConfirmAndSignInModal
// ---------------------------------------------------------------------------

function ConfirmAndSignInModal({ declarationText, busy, onCancel, onConfirm }: { declarationText: string; busy: boolean; onCancel: () => void; onConfirm: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div role="dialog" aria-modal="true" aria-label="Confirm fitness and sign in" onClick={onCancel} style={overlayCenter()}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 380, background: D.card, borderRadius: 22, padding: "22px 20px 18px", border: `1.5px solid ${D.duty}`, boxShadow: "0 16px 48px rgba(0,0,0,0.32)" }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, background: D.dutyBg, border: `1.5px solid ${D.duty}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
          <HeartIcon size={28} color={D.dutyDk} />
        </div>
        <div style={{ textAlign: "center", fontFamily: DF.body, fontSize: 10.5, fontWeight: 800, letterSpacing: "1.6px", textTransform: "uppercase", color: D.dutyDk, marginBottom: 6 }}>Declaration of fitness for duty</div>
        <div style={{ textAlign: "center", fontFamily: DF.serif, fontSize: 19, fontWeight: 700, fontStyle: "italic", color: D.ink, marginBottom: 14 }}>Please read carefully</div>
        <div style={{ background: D.dutySurf, border: `1px dashed ${D.dutyLt}`, borderRadius: 12, padding: "14px", fontFamily: DF.serif, fontSize: 14.5, fontWeight: 500, color: D.ink, lineHeight: 1.5, marginBottom: 14 }}>{declarationText}</div>
        <div style={{ fontFamily: DF.mono, fontSize: 9.5, letterSpacing: "0.6px", color: D.inkMute, textAlign: "center", marginBottom: 14 }}>Editable in System Preferences · Admin only</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onCancel} style={ghostBtnLg()}>Not now</button>
          <button onClick={onConfirm} disabled={busy} style={{ ...primaryBtn(), opacity: busy ? 0.7 : 1 }}>
            <CheckIcon size={16} color="#fff" /> Confirm &amp; Sign In
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SignOffConfirmModal (styled — never window.confirm)
// ---------------------------------------------------------------------------

function SignOffConfirmModal({ busy, onCancel, onConfirm }: { busy: boolean; onCancel: () => void; onConfirm: () => void }) {
  return (
    <div role="dialog" aria-modal="true" aria-label="Sign off duty?" onClick={onCancel} style={overlayCenter()}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 360, background: D.card, borderRadius: 22, padding: "22px 20px 18px", border: `1.5px solid ${D.duty}`, boxShadow: "0 16px 48px rgba(0,0,0,0.32)" }}>
        <div style={{ textAlign: "center", fontFamily: DF.serif, fontSize: 19, fontWeight: 700, fontStyle: "italic", color: D.ink, marginBottom: 8 }}>Sign off duty?</div>
        <div style={{ textAlign: "center", fontFamily: DF.serif, fontStyle: "italic", fontSize: 13.5, color: D.inkSoft, lineHeight: 1.5, marginBottom: 18 }}>
          This ends your shift and clears any active Lone Working check-in.
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onCancel} style={ghostBtnLg()}>Cancel</button>
          <button onClick={onConfirm} disabled={busy} style={{ ...primaryBtn(), opacity: busy ? 0.7 : 1 }}><SignOffIcon size={16} color="#fff" /> Sign off</button>
        </div>
      </div>
    </div>
  );
}

function DutyToast({ text }: { text: string }) {
  return (
    <div role="status" aria-live="polite" style={{ position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: "calc(72px + env(safe-area-inset-bottom))", background: D.ink, color: "#FFFCEC", fontFamily: DF.body, fontWeight: 700, fontSize: 13.5, padding: "10px 16px", borderRadius: 999, boxShadow: "0 10px 28px rgba(0,0,0,0.25)", zIndex: 260, letterSpacing: 0.2 }}>{text}</div>
  );
}

// ---------------------------------------------------------------------------
// Shared style + icon helpers
// ---------------------------------------------------------------------------

function useDurationMinutes(signedInAtIso: string): number {
  const startMs = useMemo(() => new Date(signedInAtIso).getTime(), [signedInAtIso]);
  const [min, setMin] = useState(() => Math.floor((Date.now() - startMs) / 60000));
  useEffect(() => {
    const tick = () => setMin(Math.floor((Date.now() - startMs) / 60000));
    const id = window.setInterval(tick, 30000);
    tick();
    return () => window.clearInterval(id);
  }, [startMs]);
  return min;
}

function overlayBottom(): React.CSSProperties {
  return { position: "fixed", inset: 0, background: "rgba(43,32,20,0.34)", backdropFilter: "blur(2px)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 200 };
}
function overlayCenter(): React.CSSProperties {
  return { position: "fixed", inset: 0, background: "rgba(43,32,20,0.44)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 220 };
}
function ghostBtnLg(): React.CSSProperties {
  return { flex: 1, minHeight: 48, padding: "12px 16px", background: "transparent", color: D.inkSoft, border: `1px solid ${D.hairline}`, borderRadius: 12, fontFamily: DF.body, fontSize: 14.5, fontWeight: 800, cursor: "pointer" };
}
function primaryBtn(): React.CSSProperties {
  return { flex: 1, minHeight: 48, padding: "12px 16px", background: D.duty, color: "#fff", border: "none", borderRadius: 12, fontFamily: DF.body, fontSize: 14.5, fontWeight: 800, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), 0 2px 6px rgba(79,104,82,0.22)" };
}
function ghostBtnSm(): React.CSSProperties {
  return { flex: 1, minHeight: 38, padding: "8px 12px", background: "transparent", color: D.inkSoft, border: `1px solid ${D.hairline}`, borderRadius: 12, fontFamily: DF.body, fontSize: 12.5, fontWeight: 800, cursor: "pointer" };
}
function brassBtnSm(): React.CSSProperties {
  return { flex: 1, minHeight: 38, padding: "8px 12px", background: D.brassDk, color: D.brassGlow, border: "none", borderRadius: 12, fontFamily: DF.body, fontSize: 12.5, fontWeight: 800, cursor: "pointer", boxShadow: "inset 0 1px 0 rgba(244,226,168,0.3), 0 2px 6px rgba(122,84,11,0.15)" };
}

function ClockIcon({ size = 16, color = "currentColor" }: { size?: number; color?: string }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>);
}
function CheckIcon({ size = 14, color = "currentColor" }: { size?: number; color?: string }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>);
}
function SignOffIcon({ size = 16, color = "currentColor" }: { size?: number; color?: string }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></svg>);
}
function HeartIcon({ size = 28, color = "currentColor" }: { size?: number; color?: string }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M19 14c1.5-1.5 3-3.5 3-5.5A4.5 4.5 0 0 0 17.5 4 6.4 6.4 0 0 0 12 7a6.4 6.4 0 0 0-5.5-3A4.5 4.5 0 0 0 2 8.5c0 2 1.5 4 3 5.5l7 7Z" /></svg>);
}
